import { useNavigate } from 'react-router-dom';
import { Lock, Shield, Mail } from 'lucide-react';
import PlatformLogo from '../../shared/PlatformLogo';
import { FOOTER_COLUMNS, FOOTER_BADGES, FOOTER_LOCATIONS, CONTACT_EMAIL } from './constants';

const BADGE_ICONS = { Lock, Shield };

// Social links carried over from the prior footer (/tmp Labs handles).
const SOCIALS = [
  { label: 'X', href: 'https://twitter.com/tmplabs', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/tmplabs', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 110-4.124 2.062 2.062 0 010 4.124zM7.119 20.452H3.555V9h3.564v11.452z' },
];

export default function NrouteFooter() {
  const navigate = useNavigate();
  const onClick = (link) => {
    if (link.type === 'route') navigate(link.href);
    else if (link.type === 'top') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const linkCls = 'text-slate-400 hover:text-[#E8A838] text-sm transition-colors';

  return (
    <footer style={{ backgroundColor: 'var(--brand-bg)' }}>
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <button onClick={() => navigate('/')} className="mb-5 flex items-center gap-2.5" aria-label="nroute home">
              <PlatformLogo size={32} showName={false} />
              <span className="text-base font-semibold text-white">
                <span style={{ color: 'var(--brand-secondary)' }}>∑n</span>-route
              </span>
            </button>
            <p className="mb-6 text-sm leading-relaxed text-slate-400">
              AI-powered study abroad. Find the right course, country and path — across 18,000+ programs in 50+ countries.
            </p>
            <div className="flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-[#E8A838]/40 hover:text-[#E8A838]"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d={s.path} /></svg>
                </a>
              ))}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                aria-label="Email"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-[#E8A838]/40 hover:text-[#E8A838]"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.type === 'external' ? (
                      <a href={link.href} className={linkCls}>{link.label}</a>
                    ) : (
                      <button onClick={() => onClick(link)} className={linkCls}>{link.label}</button>
                    )}
                  </li>
                ))}
              </ul>

              {/* Compliance badges live under the Legal column */}
              {col.title === 'Legal' && (
                <div className="mt-6 space-y-2">
                  {FOOTER_BADGES.map((b) => {
                    const Icon = BADGE_ICONS[b.icon];
                    return (
                      <div key={b.label} className="inline-flex items-center gap-2 rounded-md border border-white/12 px-3 py-1.5 text-xs text-slate-400">
                        {Icon && <Icon size={12} />}
                        {b.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-6 md:flex-row">
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} /TMP LABS PTE. LTD. All rights reserved.</p>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            {FOOTER_LOCATIONS.map((loc, i) => (
              <span key={loc} className="flex items-center gap-3">
                {i > 0 && <span>•</span>}
                {loc}
              </span>
            ))}
            <span>•</span>
            <span>Powered by /tmp Labs</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
